/**
 * Mantenimiento → Reportes (`/mantenimiento/reportes`).
 *
 * Puerto de `Maintenance_reports` + `views/maintenance/reports/*.php`: el panel
 * anterior tenía una pantalla por informe (cumplimiento, categorías, prioridades
 * y aprobación) más el panel con gráficos. Aquí se reúnen en una sola pantalla
 * con el rango de fechas delante, como en el panel anterior (del día 1 a hoy).
 *
 * Los gráficos se dibujan con CSS, igual que el resto del panel nuevo, para no
 * sumar una librería al bundle.
 */

import {
  ArrowLeft,
  BarChart3,
  CalendarRange,
  CheckCircle2,
  Download,
  RotateCcw,
  ShieldAlert,
  TriangleAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import { useActivosCatalogos } from '../activos.api';
import { useReporteMantenimiento } from '../resumen.api';
import { varianteEstado } from '../sub-activos.types';
import type { ReporteCumplimiento } from '../reportes.types';

const CONTROL =
  'h-12 w-full rounded-control border border-ink-200 bg-white px-4 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Color de la barra según la etiqueta. */
function colorBarra(valor: string): string {
  if (valor === 'CRÍTICA' || valor === 'REALIZADO') return 'bg-emerald-500';
  if (valor === 'ALTA' || valor === 'EN_PROCESO') return 'bg-amber-500';
  if (valor === 'MEDIA' || valor === 'PENDIENTE') return 'bg-rose-500';

  return 'bg-brand-500';
}

/** Fila con barra proporcional, para las distribuciones. */
function FilaBarra({
  etiqueta,
  valor,
  maximo,
  extra,
}: {
  etiqueta: string;
  valor: number;
  maximo: number;
  extra?: string;
}) {
  const ancho = maximo > 0 ? Math.max(2, Math.round((valor / maximo) * 100)) : 0;

  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-28 shrink-0 text-sm font-medium text-ink-700">{etiqueta}</span>
      <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-ink-100">
        <span
          className={cn('block h-full rounded-full', colorBarra(etiqueta))}
          style={{ width: `${ancho}%` }}
        />
      </span>
      <span className="w-24 shrink-0 text-right text-sm tabular-nums text-ink-700">
        {formatNumber(valor)}
        {extra ? <span className="block text-[11px] text-ink-400">{extra}</span> : null}
      </span>
    </div>
  );
}

/** Fila del listado de cumplimiento. */
function FilaCumplimiento({ tarea }: { tarea: ReporteCumplimiento }) {
  return (
    <tr className="border-b border-ink-50 last:border-0">
      <td className="py-2.5">
        <span className="block text-sm font-medium text-ink-800">{tarea.tipo}</span>
        <span className="block text-[11px] text-ink-400">
          {tarea.sub_activo} · {tarea.activo}
        </span>
      </td>
      <td className="py-2.5">
        <Badge
          variant={
            tarea.estado === 'REALIZADO' ? 'success' : tarea.estado === 'EN_PROCESO' ? 'brand' : 'danger'
          }
        >
          {tarea.estado}
        </Badge>
      </td>
      <td className="py-2.5 text-right text-ink-600">{tarea.programada ?? '—'}</td>
      <td className="py-2.5 text-right text-ink-600">{tarea.ejecucion ?? '—'}</td>
      <td className="py-2.5 text-right">
        {tarea.atraso === null ? (
          <span className="text-ink-300">—</span>
        ) : tarea.atraso > 0 ? (
          <Badge variant="danger">+{tarea.atraso} días</Badge>
        ) : (
          <Badge variant="success">a tiempo</Badge>
        )}
      </td>
      <td className="py-2.5 text-right">
        {tarea.aprobada ? (
          <Badge variant="success">
            <CheckCircle2 className="size-3" aria-hidden="true" />
          </Badge>
        ) : (
          <span className="text-ink-300">—</span>
        )}
      </td>
    </tr>
  );
}

export function ReportesMantenimientoPage() {
  useDocumentTitle('Mantenimiento · Reportes');

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [categoriaId, setCategoriaId] = useState(0);

  const catalogos = useActivosCatalogos();
  const reporte = useReporteMantenimiento({
    desde: desde || undefined,
    hasta: hasta || undefined,
    categoriaId: categoriaId || undefined,
  });

  const sinPermiso = reporte.error instanceof ApiError && reporte.error.status === 403;
  const datos = reporte.data;
  const categorias = catalogos.data?.categorias ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/mantenimiento"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Mantenimiento
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <BarChart3 className="size-6 text-brand-500" aria-hidden="true" />
          Reportes de mantenimiento
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Tareas del rango por estado, prioridad, categoría y día, más el cumplimiento respecto a la
          fecha programada y el estado de los sub-activos frente a su umbral.
        </p>
      </div>

      {datos ? (
        <Badge variant="outline">
          <CalendarRange className="size-3" aria-hidden="true" />
          {datos.rango.desde} → {datos.rango.hasta}
        </Badge>
      ) : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>maintenance</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const maxEstado = Math.max(...(datos?.por_estado.map((item) => item.total) ?? [0]), 1);
  const maxPrioridad = Math.max(...(datos?.por_prioridad.map((item) => item.total) ?? [0]), 1);
  const maxCategoria = Math.max(...(datos?.por_categoria.map((item) => item.total) ?? [0]), 1);
  const maxDia = Math.max(...(datos?.por_dia.map((item) => item.total) ?? [0]), 1);

  return (
    <div className="space-y-6">
      {cabecera}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="reporte-desde" className="text-sm font-medium text-ink-700">
              Desde
            </label>
            <CampoFecha
              id="reporte-desde"
             
              className={cn(CONTROL, 'w-44')}
              value={desde}
              onChange={(evento) => setDesde(evento.target.value)}
             tipo="date" />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="reporte-hasta" className="text-sm font-medium text-ink-700">
              Hasta
            </label>
            <CampoFecha
              id="reporte-hasta"
             
              className={cn(CONTROL, 'w-44')}
              value={hasta}
              onChange={(evento) => setHasta(evento.target.value)}
             tipo="date" />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="reporte-categoria" className="text-sm font-medium text-ink-700">
              Categoría
            </label>
            <Select
              id="reporte-categoria"
              className={cn(CONTROL, 'w-48')}
              value={String(categoriaId)}
              onChange={(evento) => setCategoriaId(Number(evento.target.value))}
            >
              <option value="0">Todas</option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nombre}
                </option>
              ))}
            </Select>
          </div>

          <Button
            variant="outline"
            size="sm"
            leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
            onClick={() => {
              setDesde('');
              setHasta('');
              setCategoriaId(0);
            }}
          >
            Volver al mes en curso
          </Button>

          {datos ? (
            <span className="ml-auto flex items-center gap-1 text-xs text-ink-400">
              <Download className="size-3" aria-hidden="true" />
              Se aplica sobre la fecha de creación de la tarea
            </span>
          ) : null}
        </CardContent>
      </Card>

      {reporte.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : reporte.error ? (
        <Alert variant="error" title="No se pudo generar el informe">
          {reporte.error instanceof Error ? reporte.error.message : 'La API no respondió.'}
        </Alert>
      ) : datos ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-card border border-ink-100 bg-white p-4 shadow-soft">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
                Tareas del rango
              </p>
              <p className="text-3xl font-bold text-ink-900">{formatNumber(datos.resumen.total)}</p>
              <p className="text-[11px] text-ink-400">
                {datos.resumen.pendientes} pendientes · {datos.resumen.en_proceso} en proceso
              </p>
            </div>
            <div className="rounded-card border border-emerald-100 bg-emerald-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Realizadas
              </p>
              <p className="text-3xl font-bold text-emerald-700">
                {formatNumber(datos.resumen.realizadas)}
              </p>
              <p className="text-[11px] text-emerald-700/70">
                {datos.resumen.tasa_realizacion}% del total
              </p>
            </div>
            <div className="rounded-card border border-brand-100 bg-brand-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">
                Aprobadas
              </p>
              <p className="text-3xl font-bold text-brand-700">
                {formatNumber(datos.resumen.aprobadas)}
              </p>
              <p className="text-[11px] text-brand-700/70">
                {datos.resumen.tasa_aprobacion}% de las realizadas ·{' '}
                {datos.resumen.por_aprobar} por aprobar
              </p>
            </div>
            <div className="rounded-card border border-amber-100 bg-amber-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                Cumplimiento
              </p>
              <p className="text-3xl font-bold text-amber-700">
                {datos.resumen.a_tiempo + datos.resumen.con_atraso > 0
                  ? Math.round(
                      (datos.resumen.a_tiempo /
                        (datos.resumen.a_tiempo + datos.resumen.con_atraso)) *
                        100,
                    )
                  : 0}
                %
              </p>
              <p className="text-[11px] text-amber-700/70">
                {datos.resumen.a_tiempo} a tiempo · {datos.resumen.con_atraso} con atraso
              </p>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Tareas por estado</CardTitle>
              </CardHeader>
              <CardContent>
                {datos.por_estado.map((item) => (
                  <FilaBarra
                    key={item.estado}
                    etiqueta={item.estado}
                    valor={item.total}
                    maximo={maxEstado}
                  />
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tareas por prioridad</CardTitle>
              </CardHeader>
              <CardContent>
                {datos.por_prioridad.map((item) => (
                  <FilaBarra
                    key={item.prioridad}
                    etiqueta={item.prioridad}
                    valor={item.total}
                    maximo={maxPrioridad}
                    extra={`${item.realizadas} realizadas`}
                  />
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tareas por categoría</CardTitle>
              </CardHeader>
              <CardContent>
                {datos.por_categoria.map((item) => (
                  <FilaBarra
                    key={item.id}
                    etiqueta={item.categoria}
                    valor={item.total}
                    maximo={maxCategoria}
                    extra={`${item.realizadas} realizadas`}
                  />
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sub-activos frente al umbral</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-card border border-rose-100 bg-rose-50/60 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-rose-600">
                      Críticos
                    </p>
                    <p className="text-2xl font-bold text-rose-700">{datos.sub_activos.criticos}</p>
                  </div>
                  <div className="rounded-card border border-amber-100 bg-amber-50/60 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-amber-600">
                      En aviso
                    </p>
                    <p className="text-2xl font-bold text-amber-700">{datos.sub_activos.en_aviso}</p>
                  </div>
                  <div className="rounded-card border border-emerald-100 bg-emerald-50/60 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">
                      Normales
                    </p>
                    <p className="text-2xl font-bold text-emerald-700">
                      {datos.sub_activos.normales}
                    </p>
                  </div>
                </div>

                {datos.criticos.length > 0 ? (
                  <ul className="flex flex-col gap-1.5">
                    {datos.criticos.slice(0, 5).map((subActivo) => (
                      <li
                        key={subActivo.id}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span className="min-w-0 truncate text-ink-700">
                          {subActivo.nombre} · {subActivo.activo}
                        </span>
                        <Badge variant={varianteEstado(subActivo.estado_color)}>
                          {formatNumber(subActivo.usado)} de {formatNumber(subActivo.umbral)}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Alert variant="success">Ningún sub-activo pasó su umbral.</Alert>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Tareas creadas por día</CardTitle>
            </CardHeader>
            <CardContent>
              {datos.por_dia.length === 0 ? (
                <Alert variant="info">No se crearon tareas en este rango.</Alert>
              ) : (
                <div className="flex items-end gap-2" style={{ height: 160 }}>
                  {datos.por_dia.map((dia) => (
                    <div key={dia.fecha} className="flex flex-1 flex-col items-center gap-1">
                      <span className="text-[11px] tabular-nums text-ink-500">{dia.total}</span>
                      <div
                        className="w-full rounded-t-md bg-brand-500/80"
                        style={{ height: Math.max(4, Math.round((dia.total / maxDia) * 120)) }}
                        title={`${dia.fecha}: ${dia.total}`}
                      />
                      <span className="text-[10px] text-ink-400">{dia.fecha.slice(5)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center gap-2">
                <TriangleAlert className="size-4 text-brand-500" aria-hidden="true" />
                Cumplimiento de tareas ({datos.cumplimiento.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {datos.cumplimiento.length === 0 ? (
                <Alert variant="info">No hay tareas que informar en este rango.</Alert>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                        <th className="py-2">Tarea</th>
                        <th className="py-2">Estado</th>
                        <th className="py-2 text-right">Programada</th>
                        <th className="py-2 text-right">Ejecutada</th>
                        <th className="py-2 text-right">Atraso</th>
                        <th className="py-2 text-right">Aprobada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.cumplimiento.map((tarea) => (
                        <FilaCumplimiento key={tarea.id} tarea={tarea} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
